import {
  ApiError, NetworkError, invalidateCareDay, useApi, useI18n, useLimits, type DaghfalClient, type CareEntryKind,
} from '@daghfal/core';
import { useQueryClient } from '@tanstack/react-query';
import {
  createContext, useContext, useEffect, useMemo, useState, useSyncExternalStore, type ReactNode,
} from 'react';
import { EntryQueue, type QueueEntry, type SendOutcome } from './queue';

const QueueContext = createContext<EntryQueue | null>(null);

export function QueueProvider({ children }: { children: ReactNode }) {
  const [queue] = useState(() => new EntryQueue());
  const api = useApi();
  const queryClient = useQueryClient();
  const { offlineQueue } = useLimits();
  const { t } = useI18n();
  const notPresent = t('queue.notPresent');

  // A delivered entry changes the child's day and the class board, which may be on screen beside the buttons.
  const send = useMemo(
    () => createSender(api, () => void invalidateCareDay(queryClient), notPresent),
    [api, queryClient, notPresent]);

  useEffect(() => queue.start(send, offlineQueue.maximumAttempts), [queue, send, offlineQueue.maximumAttempts]);

  return <QueueContext.Provider value={queue}>{children}</QueueContext.Provider>;
}

export function useQueue(): EntryQueue {
  const queue = useContext(QueueContext);
  if (!queue) throw new Error('useQueue was called outside a <QueueProvider>.');
  return queue;
}

/** The live queue contents. Re-renders whatever reads it whenever an entry is added, sent or fails. */
export function useQueueEntries(): QueueEntry[] {
  const queue = useQueue();
  return useSyncExternalStore(queue.subscribe, queue.snapshot, queue.snapshot);
}

/** The queue's kinds map onto the API's, except for photographs, which carry bytes and go their own way. */
const CARE_KINDS: Record<string, CareEntryKind> = {
  meal: 'Meal',
  drink: 'Drink',
  sleep: 'Sleep',
  activity: 'Activity',
  mood: 'Mood',
  diaper: 'Diaper',
  note: 'Note',
};

/**
 * Delivers one queued entry to the care endpoint.
 *
 * The entry's own `id` goes along as `clientEntryId`, which is what makes the call idempotent: a response
 * lost on the way back is retried, the server finds the entry it already wrote, and the child does not end
 * up with two lunches (FR-STF-09).
 *
 * The three outcomes are distinguished deliberately. A `NetworkError` or a 5xx is the connection and is
 * retried; a 4xx is the entry itself and is held for the teacher rather than retried into the same
 * refusal; and a photograph — which the queue records but cannot carry, since a `File` does not survive
 * `localStorage` — is deferred rather than failed.
 *
 * An entry for several children is recorded for those checked in at its time and the API names the others (NR-9).
 * The entry is then held for those others alone, so the teacher sees who is missing it; sent again once they are
 * checked in, the children already recorded are found by their idempotency key and not recorded twice.
 *
 * @param onSent Called after each delivery, so what shows the day reads it again.
 * @param notPresentReason What the pending screen says about children not checked in.
 */
export function createSender(api: DaghfalClient, onSent: () => void, notPresentReason: string) {
  return async (entry: QueueEntry): Promise<SendOutcome> => {
    if (entry.kind === 'photo') {
      // The bytes are gone once the tab closes, so a queued photograph is a marker, not a payload. The
      // upload itself happens on the photo screen, which still has the file in hand.
      return { status: 'deferred', reason: 'photo-upload-is-immediate' };
    }

    const kind = CARE_KINDS[entry.kind];
    if (!kind) return { status: 'rejected', reason: `unknown-kind:${entry.kind}` };

    try {
      const recorded = await api.daily.record({
        kind,
        childIds: entry.childIds,
        occurredAt: entry.recordedAt,
        clientEntryId: entry.id,
        ...entry.payload,
      });
      onSent();
      return recorded.notPresent?.length
        ? { status: 'rejected', reason: 'not-present', message: notPresentReason, childIds: recorded.notPresent }
        : { status: 'sent' };
    } catch (cause) {
      if (cause instanceof NetworkError) return { status: 'retry', reason: 'offline' };

      if (cause instanceof ApiError) {
        // The API's problem text is already written in plain Arabic and says what to do next, so it is
        // carried through to the pending screen rather than replaced with a status code.
        const message = cause.problem.detail ?? cause.problem.title ?? undefined;
        const reason = message ?? String(cause.status);
        return cause.status >= 500
          ? { status: 'retry', reason }
          : { status: 'rejected', reason, message };
      }

      return { status: 'retry', reason: cause instanceof Error ? cause.message : 'send-failed' };
    }
  };
}
